import api from "./client";
import { openAuthenticatedPdf } from "./pdf";

export const getBookings = (params) => api.get("/bookings", { params });
export const getBooking = (bookingId) => api.get(`/bookings/${bookingId}`);
export const createBooking = (data) => api.post("/bookings", data);
export const updateBooking = (bookingId, data, config = {}) =>
  api.patch(`/bookings/${bookingId}`, data, config);
export const updateBookingStatus = (bookingId, status) =>
  api.patch(`/bookings/${bookingId}/status`, null, { params: { status } });
export const deleteBooking = (bookingId) => api.delete(`/bookings/${bookingId}`);
// Duplicate a booking (same enquiry/client/supplier/dates/prices, fresh
// invoices + confirmations). Used for "two apartments in the same building".
// `options` answers the pre-duplicate prompt: { keep_dates, keep_rates,
// check_in, check_out, supplier_nightly_rate, client_nightly_rate,
// invoice_grouping }. Omitted = keep everything (previous behaviour).
export const duplicateBooking = (bookingId, options = {}) =>
  api.post(`/bookings/${bookingId}/duplicate`, options);
// supplier_cancellation_fee = what the supplier charges us (blank = not known
// yet). It was dropped here until 2026-10-07, so no booking ever stored it.
export const cancelBooking = (bookingId, { cancellation_fee, supplier_cancellation_fee, void_invoices } = {}) =>
  api.post(`/bookings/${bookingId}/cancel`, { cancellation_fee, supplier_cancellation_fee, void_invoices });
export const extendBooking = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/extend`, data);
export const undoExtension = (bookingId) =>
  api.post(`/bookings/${bookingId}/undo-extension`);
export const completeExtensionConfirmation = (bookingId) =>
  api.post(`/bookings/${bookingId}/extension/complete`);
export const toggleRolling = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/toggle-rolling`, data);
// Arrival postponed: booking and dates stay, check-in + guest automations pause.
export const postponeArrival = (bookingId, { note, expected_arrival_date } = {}) =>
  api.post(`/bookings/${bookingId}/arrival-postponed`, { note, expected_arrival_date });
export const resumeArrival = (bookingId, { restart_checkin_chaser } = {}) =>
  api.post(`/bookings/${bookingId}/arrival-resumed`, { restart_checkin_chaser });
export const openBookingConfirmationPdf = (bookingId, type) =>
  openAuthenticatedPdf(`/bookings/${bookingId}/confirmation/${type}`);
export const previewBookingConfirmation = (bookingId, confType) =>
  api.post(`/bookings/${bookingId}/confirmation/preview`, { conf_type: confType });
export const sendBookingConfirmation = (bookingId, confType, { subject, html, invoice_ids, cc, invoice_drafts } = {}) =>
  api.post(`/bookings/${bookingId}/confirmation/send`, { conf_type: confType, subject, html, invoice_ids, cc, invoice_drafts }, { timeout: 120000 });
export const createBookingInvoices = (bookingId) =>
  api.post(`/bookings/${bookingId}/create-invoices`);
export const adjustInvoicesForDateChange = (bookingId, data = {}) =>
  api.post(`/bookings/${bookingId}/adjust-invoices`, data);
// Strategy C (Issue 2 Part 2): dry-run invoice preview for the Booking
// Confirmation Review wizard. Returns draft invoice objects without any
// database writes, Xero push, or Drive upload. The wizard passes the
// (possibly edited) drafts back to sendBookingConfirmation which then
// persists them atomically as part of the final send step.
export const previewBookingInvoices = (bookingId) =>
  api.post(`/bookings/${bookingId}/confirmation/invoice-preview`);
export const getBookingActivities = (bookingId) =>
  api.get(`/bookings/${bookingId}/activities`);
export const previewUpdatedInvoiceEmail = (bookingId, invoiceId, multi = false) =>
  api.post(`/bookings/${bookingId}/updated-invoice-email/preview`, { invoice_id: invoiceId, multi: !!multi });
export const sendUpdatedInvoiceEmail = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/updated-invoice-email/send`, data);
export const getMultiLocationInvoiceStatus = (bookingId) =>
  api.get(`/bookings/${bookingId}/multi-location-invoice-status`);
export const sendMultiLocationUpdatedInvoiceEmail = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/updated-invoice-email/send-multi`, data);
export const previewGuestDetailsChaser = (bookingId) =>
  api.get(`/bookings/${bookingId}/guest-details-chaser/preview`);
export const sendGuestDetailsChaser = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/guest-details-chaser/send`, data);
export const previewGuestIdChaser = (bookingId) =>
  api.get(`/bookings/${bookingId}/guest-id-chaser/preview`);
export const sendGuestIdChaser = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/guest-id-chaser/send`, data);
export const previewCheckinChaser = (bookingId) =>
  api.get(`/bookings/${bookingId}/checkin-chaser/preview`);
export const sendCheckinChaser = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/checkin-chaser/send`, data);
export const previewPropertyReportChaser = (bookingId) =>
  api.get(`/bookings/${bookingId}/property-report-chaser/preview`);
export const sendPropertyReportChaser = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/property-report-chaser/send`, data);
export const updateDepositStatus = (bookingId, data) => api.patch(`/bookings/${bookingId}/deposit-status`, data);
export const previewDepositReminder = (bookingId) => api.get(`/bookings/${bookingId}/deposit-reminder/preview`);
export const sendDepositReminder = (bookingId, data) => api.post(`/bookings/${bookingId}/deposit-reminder/send`, data);
export const uploadGuestIdDocuments = (bookingId, files) => {
  const formData = new FormData();
  for (const f of files) formData.append("files", f);
  return api.post(`/bookings/${bookingId}/guest-id-documents`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const confirmBatch = (items, { reject_others, send_decline_emails, spare_quote_ids, invoice_grouping } = {}) =>
  api.post("/bookings/confirm-batch", { items, reject_others, send_decline_emails, spare_quote_ids, invoice_grouping });
export const previewBatchInvoices = (bookingIds, { parentEnquiryId } = {}) =>
  api.post("/bookings/batch-confirmation/invoice-preview", { booking_ids: bookingIds, parent_enquiry_id: parentEnquiryId });
export const previewBatchConfirmation = (bookingIds, { parentEnquiryId } = {}) =>
  api.post("/bookings/batch-confirmation/preview", { booking_ids: bookingIds, parent_enquiry_id: parentEnquiryId });
export const sendBatchConfirmation = (bookingIds, { subject, cc, parentEnquiryId, invoiceDrafts } = {}) =>
  api.post("/bookings/batch-confirmation/send", { booking_ids: bookingIds, subject, cc, parent_enquiry_id: parentEnquiryId, invoice_drafts: invoiceDrafts }, { timeout: 120000 });
export const getWhatsAppPreview = (bookingId) => api.get(`/bookings/${bookingId}/whatsapp-preview`);
export const getGuestComms = (bookingId) => api.get("/guest-communications", { params: { booking_id: bookingId } });
export const sendGuestMessage = (data) => api.post("/guest-communications/send", data);
export const regenerateGuestDraft = (commId) => api.post(`/guest-communications/${commId}/regenerate-draft`);
export const generateGuestDraft = (data) => api.post("/guest-communications/generate-draft", data);
export const getClientChangePreview = (bookingId, newClientId) =>
  api.get(`/bookings/${bookingId}/client-change-preview`, { params: { new_client_id: newClientId } });
export const relinkBookingClient = (bookingId, data) =>
  api.post(`/bookings/${bookingId}/relink-client`, data);
export const getBookingReconciliation = (bookingId) =>
  api.get(`/bookings/${bookingId}/reconciliation`);
export const getBookingSegments = (bookingId) =>
  api.get(`/bookings/${bookingId}/segments`);
// Check-in photos (entrance, key box, parking): sent inline in the check-in email.
export const listCheckinPhotos = (bookingId) => api.get(`/bookings/${bookingId}/checkin-photos`);
export const uploadCheckinPhotos = (bookingId, files, caption = "") => {
  const formData = new FormData();
  for (const f of files) formData.append("files", f);
  if (caption) formData.append("caption", caption);
  return api.post(`/bookings/${bookingId}/checkin-photos`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
};
export const getCheckinPhotoBlob = (bookingId, photoId) =>
  api.get(`/bookings/${bookingId}/checkin-photos/${photoId}`, { responseType: "blob" });
export const deleteCheckinPhoto = (bookingId, photoId) =>
  api.delete(`/bookings/${bookingId}/checkin-photos/${photoId}`);

// Client-side messages of a booking, both directions (Fan, 2026-10-06).
export const getBookingClientCommunications = (bookingId, params = {}) =>
  api.get(`/bookings/${bookingId}/client-communications`, { params });
